import React, { useCallback, useMemo } from 'react'
import Dropzone from 'react-dropzone'
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { formatBytes } from 'src/lib/formatBytes'
import { CheckCircle } from '@mui/icons-material'
import { useToasts } from 'react-toast-notifications'
import FilenameCell from 'src/participantHistoryForm/form/patientFilesUpload/FilenameCell'
import { PatientFileFragment, useCreatePatientFileMutation } from './patientFiles.generated'
import useRenameFile from './patientFilesUpload/useRenameFile'

type Props = {
  uid: string
  state: PatientFilesUploadState
  onChange: React.Dispatch<React.SetStateAction<PatientFilesUploadState>>
}

export type DoneUploadItem = Extract<UploadItem, { status: 'done' }>

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024

const PatientFilesUpload = ({ uid, state, onChange }: Props) => {
  const [createPatientFile] = useCreatePatientFileMutation()
  const { addToast } = useToasts()
  const { activeRename, setActiveRename, renameFile, handleRenameUnexpectedError, renameInputRef } = useRenameFile({
    onChange,
  })

  const uploadsInProgress = useMemo(() => state.some((i) => i.status === 'uploading'), [state])

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return
      const localIDs = files.map(() => crypto.randomUUID())
      onChange((prev) => [
        ...prev.filter(
          (item) =>
            item.status !== 'error' ||
            !files.some((file) => file.name === item.file.filename && file.size === item.file.size)
        ),
        ...files.map((file, idx) => ({
          localID: localIDs[idx],
          file: {
            filename: file.name,
            size: file.size,
          },
          status: 'uploading' as const,
        })),
      ])

      await Promise.all(
        files.map(async (file, idx) => {
          const localID = localIDs[idx]
          try {
            const contentType = guessContentType(file)
            const result = await createPatientFile({
              variables: {
                uid,
                input: {
                  filename: file.name,
                  filesize: file.size,
                  contentType,
                },
              },
            })

            const payload = result.data?.createPatientFile
            if (!payload) throw new Error('Failed to create upload URL')

            const uploadResponse = await fetch(payload.uploadURL, {
              method: payload.method,
              headers: {
                'Content-Type': contentType,
                'x-goog-meta-filename': encodeURIComponent(file.name),
              },
              body: file,
            })
            if (!uploadResponse.ok) throw new Error(`Upload failed (${uploadResponse.status})`)

            onChange((prev) =>
              prev.map((i) => (i.localID === localID ? { ...i, status: 'done', file: payload.file } : i))
            )
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Upload failed'
            onChange((prev) =>
              prev.map((i) => (i.localID === localID ? { ...i, status: 'error', errorMessage: message } : i))
            )
          }
        })
      )
    },
    [createPatientFile, uid, onChange]
  )

  const onFilesSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget
      const fileList = input.files
      if (!fileList || fileList.length === 0) return
      const files = Array.from(fileList)
      input.value = ''
      await uploadFiles(files)
    },
    [uploadFiles]
  )

  return (
    <Box mt={2}>
      <Dropzone
        accept={acceptForDropzone()}
        maxFiles={50}
        disabled={uploadsInProgress}
        multiple
        onDropAccepted={(acceptedFiles) => uploadFiles(acceptedFiles)}
        onDropRejected={() => {
          addToast('Failed to add files. Please check the file types and sizes.', {
            appearance: 'error',
          })
        }}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <Box
            {...getRootProps()}
            sx={{
              border: '1px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              borderRadius: 1,
              p: 2,
              cursor: uploadsInProgress ? 'not-allowed' : 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
            }}
          >
            <input {...getInputProps()} />
            <Typography variant="body2">
              {isDragActive ? 'Drop files here…' : 'Drag ’n’ drop files here, or click to select.'}
            </Typography>
            <Typography variant="body2">
              Supported file types: {Object.keys(EXTENSIONS).join(', ')}. Max file size:{' '}
              {formatBytes(MAX_FILE_SIZE_BYTES)}
            </Typography>

            <Box mt={1}>
              <Button variant="outlined" component="span" disabled={uploadsInProgress}>
                Add file
              </Button>
              <input hidden multiple type="file" onChange={onFilesSelected} />
            </Box>

            {state.length > 0 && (
              <Box mt={2}>
                <Table size="small" aria-label="patient history files">
                  <TableHead>
                    <TableRow>
                      <TableCell>Filename</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" width={1}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.map((item) => (
                      <TableRow key={item.localID}>
                        <TableCell>
                          <FilenameCell
                            item={item}
                            activeRename={activeRename}
                            renameInputRef={renameInputRef}
                            onRenameFilenameChange={(newFilename: string) =>
                              setActiveRename((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      newFilename,
                                      error: undefined,
                                    }
                                  : prev
                              )
                            }
                            onCancelRename={() => setActiveRename(null)}
                            onStartRename={(doneItem: DoneUploadItem) =>
                              setActiveRename({
                                fileID: doneItem.file.fileID,
                                newFilename: doneItem.file.filename,
                                isLoading: false,
                              })
                            }
                            onSaveRename={(doneItem: DoneUploadItem) =>
                              renameFile(doneItem).catch(handleRenameUnexpectedError)
                            }
                          />
                        </TableCell>
                        <TableCell align="right">{formatBytes(item.file.size)}</TableCell>
                        <TableCell>
                          {item.status === 'uploading' && <CircularProgress size={16} />}
                          {item.status === 'done' &&
                            (activeRename?.fileID === item.file.fileID && activeRename.isLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckCircle />
                            ))}
                          {item.status === 'error' && (
                            <Typography variant="body2" color="error">
                              {item.errorMessage}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              onChange((prev) => prev.filter((i) => i.localID !== item.localID))
                              e.stopPropagation()
                            }}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Dropzone>
    </Box>
  )
}

export type PatientFilesUploadState = UploadItem[]

export type UploadItem =
  | {
      localID: string
      file: {
        filename: string
        size: number
      }
      status: 'uploading'
    }
  | {
      localID: string
      status: 'done'
      file: PatientFileFragment
    }
  | {
      localID: string
      file: {
        filename: string
        size: number
      }
      status: 'error'
      errorMessage: string
    }

// Restricted to types embeddable in the medical record: PNG, JPEG, and PDF.
const EXTENSIONS = {
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
}

function acceptForDropzone(): Record<string, string[]> {
  return Object.entries(EXTENSIONS).reduce<Record<string, string[]>>((acc, [ext, mime]) => {
    if (!acc[mime]) acc[mime] = []
    acc[mime].push(`.${ext}`)
    return acc
  }, {})
}

function guessContentType(file: File): string {
  if (file.type) return file.type
  const lower = file.name.toLowerCase()
  const extension = lower.split('.').pop()
  if (extension && EXTENSIONS[extension as keyof typeof EXTENSIONS])
    return EXTENSIONS[extension as keyof typeof EXTENSIONS]
  return 'application/octet-stream'
}

export default PatientFilesUpload
